/**
 * 去品牌化 M4 — 寫入路徑「目標品牌」解析純函數。
 *
 * 品牌隔離紅線：所有品牌隔離資源的寫入都要顯式帶目標品牌（X-Target-Brand header +
 * body.targetBrand），**絕不繼承檢視範圍（X-Brand）、也絕不 fallback JWT activeBrand**。
 *
 * 兩種來源（依端點形狀擇一）：
 *   1. 既有 row 寫入（PUT/DELETE/PATCH /:id、或隸屬某 row 的子資源）
 *      → 目標品牌 = 該 row 的品牌（列表已知的 `row.brand`）。用 {@link targetBrandFromRow}。
 *   2. 檢視範圍推導寫入（清單以 X-Brand 過濾、row 無獨立 brand 欄，如 versions / ota /
 *      promo-codes）→ 目標品牌 = 目前「單一品牌檢視」；聚合檢視（null）無法判定 →
 *      fail-closed 擋下，不繼承 activeBrand。用 {@link targetBrandFromViewScope}。
 *
 * 本模組純函數、不依賴 React / AntD，方便單元測試直接驗證 fail-closed 行為。
 */

/** 解析結果：可判定則帶品牌，否則帶擋下原因（供 UI 提示）。 */
export type TargetBrandResolution =
  | { ok: true; brand: string }
  | { ok: false; reason: string };

/**
 * 既有 row 寫入的目標品牌 = 該 row 的品牌。
 * row.brand 缺失（理論上不會發生：列表都帶 brand 欄）→ fail-closed 擋下。
 */
export function targetBrandFromRow(
  rowBrand: string | null | undefined,
): TargetBrandResolution {
  if (rowBrand && rowBrand.length > 0) {
    return { ok: true, brand: rowBrand };
  }
  return {
    ok: false,
    reason: '無法判定這筆資料的品牌，請重新整理列表後再操作。',
  };
}

/**
 * 檢視範圍推導寫入的目標品牌 = 目前單一品牌檢視。
 * 聚合檢視（brandForRequest 為 null）→ fail-closed 擋下，提示切單一品牌。
 */
export function targetBrandFromViewScope(
  brandForRequest: string | null | undefined,
): TargetBrandResolution {
  if (brandForRequest && brandForRequest.length > 0) {
    return { ok: true, brand: brandForRequest };
  }
  return {
    ok: false,
    reason: '請先在右上角「檢視範圍」切換到單一品牌，再進行此寫入操作。',
  };
}

/**
 * 新增/編輯共用表單（upsert）的目標品牌解析。
 *
 * 品牌隔離紅線：寫入絕不繼承檢視範圍。因此依「是否有既有 row」二分：
 *   - 編輯既有 row（有 rowBrand）→ 目標品牌 = 該 row 的品牌（{@link targetBrandFromRow}），
 *     即使當下檢視是別的品牌也只認 row.brand，對齊 backend
 *     `resolveExistingRowWriteTargetBrand`（顯式與 row 不符 → 403、缺顯式 enforce ON → 400）。
 *   - 新增（無既有 row）→ 目標品牌 = 目前單一品牌檢視（{@link targetBrandFromViewScope}），
 *     聚合檢視 fail-closed 擋下。
 *
 * @param isEdit    是否為編輯既有 row。
 * @param rowBrand  編輯時該 row 的品牌（列表已帶 brand 欄）；新增時忽略。
 * @param viewBrand 目前單一品牌檢視（聚合為 null）；新增時作為目標品牌。
 */
export function targetBrandForUpsert(
  isEdit: boolean,
  rowBrand: string | null | undefined,
  viewBrand: string | null | undefined,
): TargetBrandResolution {
  return isEdit
    ? targetBrandFromRow(rowBrand)
    : targetBrandFromViewScope(viewBrand);
}

/**
 * 把解析出的目標品牌組成寫入請求要帶的 header + body 欄位。
 *
 * 契約對齊 backend `extractTargetBrand`（strict_brand_scope.middleware）：
 *   - header：`X-Target-Brand: <brand>`
 *   - body：`{ targetBrand: <brand> }`（multipart 不便塞 body 時以 header 為準）
 * 兩通道皆為顯式 targetBrand 專用 key，backend 只認它、不被泛用 brand/X-Brand 蓋過。
 */
export function targetBrandHeader(brand: string): Record<string, string> {
  return { 'X-Target-Brand': brand };
}
